import { createQueryKey } from '../query/createQueryKeyFactory';
import { QueryKey, QueryKeyParam } from '../query/types';
import { accountCodeKeys } from './accountCodes/keys';
import { communicationOfficersKeys } from './communication/officers/keys';
import { consumerWalletKeys } from './consumer/wallet/keys';
import { duesRecipientsKeys } from './dues/recipients/keys';
import { duesPaymentsKeys } from './duesPayments/keys';
import { formKeys } from './forms/keys';
import { membershipKeys } from './memberships/keys';
import { officerTypeKeys } from './officerTypes/keys';
import { publishPermissionsKeys } from './publishPermissions/keys';
import { reportKeys } from './reports/keys';
import { salesTaxPermissionsKeys } from './salesTax/keys';
import { submissionsKeys } from './submissions/keys';
import { parentsKeys } from './units/parents/keys';

const causesKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys, 'causes'] as const,
    id: <U extends QueryKeyParam>(itemId: U) =>
      createQueryKey({
        all: [...keys, 'causes', itemId] as const,
        detail: [...keys, 'causes', itemId, 'detail'] as const,
      }),
  });

const unitKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys, 'units'] as const,
    id: <U extends QueryKeyParam>(itemId: U) =>
      createQueryKey({
        all: [...keys, 'units', itemId] as const,
        hierarchy: [...keys, 'hierarchy', itemId] as const,
        parents: parentsKeys([...keys, 'units', itemId] as const),
        parent: <V extends QueryKeyParam>(parentId: V) =>
          createQueryKey({
            all: [...keys, 'units', itemId, 'parent', parentId] as const,
          }),
      }),
    export: [...keys, 'export'] as const,
  });

// scopeId refers to the causeId
const complianceIdKey = <T extends QueryKey, U extends QueryKeyParam>(keys: T, scopeId: U) =>
  createQueryKey({
    all: [...keys, scopeId] as const,
    detail: [...keys, scopeId, 'detail'] as const,
    accountCodes: accountCodeKeys([...keys, scopeId] as const),
    forms: formKeys([...keys, scopeId] as const),
    causes: causesKeys([...keys, scopeId] as const),
    reports: reportKeys([...keys, scopeId] as const),
    memberships: membershipKeys([...keys, scopeId] as const),
    publishPermissions: publishPermissionsKeys([...keys, scopeId] as const),
    salesTaxPermissions: salesTaxPermissionsKeys([...keys, scopeId] as const),
    submissions: submissionsKeys([...keys, scopeId] as const),
    officerTypes: officerTypeKeys([...keys, scopeId] as const),
    units: unitKeys([...keys, scopeId] as const),
    communicationOfficers: communicationOfficersKeys([...keys, scopeId] as const),
    duesPayments: duesPaymentsKeys([...keys, scopeId] as const),
    duesRecipients: duesRecipientsKeys([...keys, scopeId] as const),
  });

const compKeys = createQueryKey({
  all: ['compliance'] as const,
  id: <T extends QueryKeyParam>(id: T) => complianceIdKey(['compliance'] as const, id),
  consumerWallet: consumerWalletKeys(['compliance'] as const),
});

// Note: you can uncomment this below to see the intellisense work.
// const testKey = compKeys.id('test').accountCodes.id('test').all;
export { compKeys };
